import { useState, useEffect } from 'react'
import { Divider } from 'components/divider'
import { QuestionMarkCircleIcon, EyeIcon, ArrowLeftIcon, EllipsisVerticalIcon } from '@heroicons/react/20/solid'
import { t } from 'i18next'
import { Badge } from 'components/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/table'
import { Dropdown, DropdownButton, DropdownItem, DropdownLabel, DropdownMenu } from 'components/dropdown'
import { useNavigate } from 'react-router-dom'
import Flatpickr from 'react-flatpickr'
import { ErrorDialog, SuccessDialog } from 'components/custom/ResponseDialog'
import { Pagination, PaginationList, PaginationNext, PaginationPage, PaginationPrevious } from 'components/pagination'
import { GET_FEEDBACK_LIST } from 'graphql/common/queries'
import loadingImage from '../../assets/images/boat-loading.gif'
import { useLazyQuery } from '@apollo/client'
import moment from 'moment'
import { getPageRange } from 'utils/paginationHelper'

export default function Feedback() {
    const { filterObj: initialFilterObj } = {}
    const defaultFilter = initialFilterObj || {
        adminAction: '',
        dateFrom: moment().subtract(1, 'months').format('YYYY-MM-DD'),
        dateTo: moment().format('YYYY-MM-DD'),
    }
    const [filterObj, setFilterObj] = useState(defaultFilter)
    const [pendingFilterObj, setPendingFilterObj] = useState(defaultFilter)
    const navigate = useNavigate()
    const [feedbackList, setFeedbackList] = useState(null)
    const [feedbackCount, setFeedbackCount] = useState(0)

    // pagination
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const totalPages = Math.ceil(feedbackCount / rowsPerPage)

    const [getFeedbackList, { loading: feedbackListLoading, error: feedbackListError }] = useLazyQuery(
        GET_FEEDBACK_LIST,
        {
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
            onCompleted: data => {
                if (data.getFeedbackResponses.success && data.getFeedbackResponses.code === '200') {
                    setFeedbackList(data.getFeedbackResponses.feedback_responses)
                    setFeedbackCount(data.getFeedbackResponses.total_count || 10)
                }
            },
        }
    )

    // Search boats Boat List
    const searchFeedback = () => {
        console.log('searchFeedback', filterObj)
        getFeedbackList({
            variables: {
                page: {
                    limit: rowsPerPage,
                    offset: page * rowsPerPage,
                },
                filter: {
                    from_date: filterObj.dateFrom || moment().subtract(1, 'months').format('YYYY-MM-DD'),
                    to_date: filterObj.dateTo || moment().format('YYYY-MM-DD'),
                    admin_action:
                        filterObj.adminAction === 'true' ? true : filterObj.adminAction === 'false' ? false : null,
                },
            },
        })
    }

    useEffect(() => {
        searchFeedback()
    }, [page, rowsPerPage, filterObj]) // Only call API when filterObj changes

    const handlePageChange = newPage => {
        setPage(newPage)
    }
    const onClickSearch = () => {
        setPage(0)
        setRowsPerPage(10)
        setFilterObj({ ...pendingFilterObj }) // update filterObj, triggers API call
    }
    const onClickSearchClear = () => {
        const cleared = {
            adminAction: '',
            dateFrom: moment().subtract(1, 'months').format('YYYY-MM-DD'),
            dateTo: moment().format('YYYY-MM-DD'),
        }
        setPendingFilterObj(cleared)
        setPage(0)
        setRowsPerPage(10)
        setFilterObj(cleared) // triggers API call with cleared filters
    }

    const [showErrDialog, setShowErrDialog] = useState(false)
    const [showSucDialog, setShowSucDialog] = useState(false)
    const [dialogMessage, setDialogMessage] = useState('')

    const handleErrClose = () => {
        setShowErrDialog(false)
        setDialogMessage('')
    }
    const handleSucClose = () => {
        setShowSucDialog(false)
        setDialogMessage('')
    }

    return (
        <>
            <div className="mb-6 flex justify-between items-center">
                <button
                    onClick={() => {
                        navigate('/')
                    }}
                    type="button"
                    className="inline-flex items-center gap-x-1.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Home')}
                </button>
            </div>
            <div className="flex flex-wrap items-end justify-between gap-4 mb-4">
                <div className="max-sm:w-full sm:flex-1">
                    <div className="flex flex-wrap items-center gap-6 sm:flex-nowrap justify-between"></div>
                    <div className="flex flex-wrap items-center sm:flex-nowrap">
                        <div className="mx-auto flex flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 bg-[#e9eff1] w-[40px] h-[40px]">
                            <QuestionMarkCircleIcon className="w-[20px] h-[20px] fill-[#285f74]" />
                        </div>
                        <div>
                            <span className="ms-3"> {t('Feedbacks')}</span>
                            <p className="ms-3 text-sm font-normal leading-6 weight-[200] text-gray-500">
                                {t('View and manage feedback submitted by mobile app users.')}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="w-12/12 xl:w-9/12 mt-4 mb-4 flex flex-col sm:flex-row gap-4">
                <div className="w-12/12 md:w-3/12">
                    <label htmlFor="adminAction" className="block text-sm font-medium leading-6 text-gray-900">
                        {t('Admin Action')}
                    </label>
                    <div className="mt-1">
                        <select
                            id="adminAction"
                            name="adminAction"
                            value={pendingFilterObj.adminAction}
                            onChange={e => setPendingFilterObj({ ...pendingFilterObj, adminAction: e.target.value })}
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        >
                            <option value="">{t('All')}</option>
                            <option value={true}>{t('Completed')}</option>
                            <option value={false}>{t('Pending')}</option>
                        </select>
                    </div>
                </div>

                <div className="w-12/12 md:w-4/12">
                    <label htmlFor="dateRange" className="block text-sm font-medium leading-6 text-gray-900">
                        {t('Date Range')}
                    </label>
                    <div className="mt-1">
                        <Flatpickr
                            id="dateRange"
                            name="dateRange"
                            options={{
                                mode: 'range',
                                dateFormat: 'Y-m-d',
                            }}
                            value={[pendingFilterObj.dateFrom, pendingFilterObj.dateTo]}
                            onChange={date => {
                                setPendingFilterObj({
                                    ...pendingFilterObj,
                                    dateFrom: date[0] ? date[0].toLocaleDateString('en-CA').replace(/-/g, '-') : '',
                                    dateTo: date[1] ? date[1].toLocaleDateString('en-CA').replace(/-/g, '-') : '',
                                })
                            }}
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                        />
                    </div>
                </div>
                <div className="flex-none grow gap-2 justify-end place-content-end">
                    <button
                        onClick={onClickSearch}
                        type="button"
                        className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9]"
                    >
                        {t('Search')}
                    </button>
                    <button
                        onClick={onClickSearchClear}
                        type="button"
                        className="ms-3 inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                    >
                        {t('Clear')}
                    </button>
                </div>
            </div>

            <Divider />
            {feedbackListLoading && (
                <div className="flex flex-col justify-center items-center bg-white h-96 mt-16">
                    <img alt="logo" src={loadingImage} className="w-[250px]" />
                    <p className="pt-3 text-primary font-semibold">Loading, please wait...</p>
                </div>
            )}
            {feedbackListError && (
                <div className="flex justify-center items-center bg-white py-5">
                    <div className="text-lg text-center">{t('Failed to Fetch Records!')}</div>
                </div>
            )}
            {feedbackList?.length === 0 && !feedbackListLoading && !feedbackListError && (
                <div className="flex justify-center items-center bg-white py-5">
                    <div className="text-lg text-center">{t('No Records Found!')}</div>
                </div>
            )}
            {!feedbackListLoading && !feedbackListError && feedbackList?.length > 0 && (
                <>
                    <div className="table-custom overflow-x-scroll lg:overflow-hidden rounded-b-lg bg-white p-0 flex mt-2">
                        <Table className="[--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
                            <TableHead>
                                <TableRow>
                                    <TableHeader>{t('Travel Date')}</TableHeader>
                                    <TableHeader>{t('Questions')}</TableHeader>
                                    <TableHeader>{t('Submitted On')}</TableHeader>
                                    <TableHeader>{t('Admin Action')}</TableHeader>
                                    <TableHeader>{t('Action')}</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {feedbackList?.map((feedback, index) => (
                                    <TableRow
                                        key={feedback.response_id}
                                        className="cursor-pointer hover:bg-gray-50"
                                        onClick={() =>
                                            navigate(`/feedback/details/${feedback.response_id}`, {
                                                state: { feedbackInfo: feedback },
                                            })
                                        }
                                    >
                                        <TableCell>{feedback.response_info.travel_date}</TableCell>
                                        <TableCell>{feedback.response.length}</TableCell>
                                        <TableCell>{feedback.submitted_at}</TableCell>
                                        <TableCell>
                                            <Badge color={feedback.admin_action == true ? 'emerald' : 'zinc'}>
                                                {feedback.admin_action.action_taken ? 'Completed' : 'Pending'}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            <Dropdown>
                                                <DropdownButton
                                                    plain
                                                    aria-label="More options"
                                                    onClick={e => e.stopPropagation()}
                                                    className="cursor-pointer"
                                                >
                                                    <EllipsisVerticalIcon />
                                                </DropdownButton>
                                                <DropdownMenu anchor="bottom">
                                                    <DropdownItem
                                                        onClick={e => {
                                                            e.stopPropagation()
                                                            navigate(`/feedback/details/${feedback.response_id}`, {
                                                                state: { feedbackInfo: feedback },
                                                            })
                                                        }}
                                                    >
                                                        <EyeIcon />
                                                        <DropdownLabel>{t('View')}</DropdownLabel>
                                                    </DropdownItem>
                                                </DropdownMenu>
                                            </Dropdown>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                    <Divider />
                    <Pagination className="py-6">
                        <PaginationPrevious
                            onClick={() => handlePageChange(page > 0 ? page - 1 : 0)}
                            disabled={page === 0}
                        />
                        <PaginationList>
                            {getPageRange(page, totalPages).map(index => (
                                <PaginationPage
                                    key={index}
                                    current={index === page}
                                    onClick={() => handlePageChange(index)}
                                >
                                    {index + 1}
                                </PaginationPage>
                            ))}
                        </PaginationList>
                        <PaginationNext
                            onClick={() => handlePageChange(page < totalPages - 1 ? page + 1 : totalPages - 1)}
                            disabled={page === totalPages - 1}
                        />
                    </Pagination>
                    <Divider />
                </>
            )}
            {showErrDialog && (
                <ErrorDialog status={true} title="Error" message={dialogMessage} onClose={handleErrClose} />
            )}
            {showSucDialog && (
                <SuccessDialog status={true} title="Success" message={dialogMessage} onClose={handleSucClose} />
            )}
        </>
    )
}
